import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { Underline } from "@tiptap/extension-underline";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";

import type { TiptapEditorProps } from ".";

export function useInitializeEditor({
  borderless,
  className,
  isDisabled,
  isMinimal,
  isReadOnly = false,
  onChange,
  onChangeHTML,
  placeholder,
  value,
  ...editorProps
}: {
  borderless?: boolean;
  className?: string;
  isReadOnly?: boolean;
} & TiptapEditorProps) {
  return useEditor({
    content: value,
    editable: !isDisabled && !isReadOnly,
    editorProps: {
      attributes: {
        class: twMerge(
          clsx(
            "prose h-full max-w-full overflow-hidden overflow-y-auto text-text-primary text-md leading-tight outline-none marker:text-text-primary",
            !borderless && "min-h-[124px] rounded-md px-[14px] py-lg",
            isDisabled && "bg-bg-disabled-subtle text-text-disabled",
            !isDisabled && !borderless && "bg-bg-primary",
            className,
          ),
        ),
        "data-testid": "tiptap-editor",
      },
    },
    extensions: [
      isMinimal
        ? StarterKit.configure({
            blockquote: false,
            code: false,
            codeBlock: false,
            heading: false,
            horizontalRule: false,
            strike: false,
          })
        : StarterKit,
      Placeholder.configure({
        emptyEditorClass:
          "cursor-text before:pointer-events-none before:absolute before:inset-x-[14px] before:text-text-placeholder before:content-[attr(data-placeholder)]",
        placeholder: placeholder,
        showOnlyWhenEditable: false,
      }),
      ...(isMinimal
        ? []
        : [
            Link.extend({ inclusive: false }).configure({
              HTMLAttributes: {
                class: "underline text-blue-700 text-md font-normal",
              },
            }),
            Underline,
          ]),
    ],
    onUpdate: ({ editor }) => {
      onChange?.(editor.getText());
      onChangeHTML?.(editor.getHTML());
    },
    ...editorProps,
  });
}
